import snakeCase from "lodash/snakeCase";
import {
  Blocks,
  Gavel,
  Gem,
  Mail,
  Megaphone,
  PhoneCall,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  StickyNote,
  Tags,
} from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import CONFIG from "Config";
import {
  SidebarNavItem,
  SidebarSection,
} from "components/sidebar/sections/SidebarNav";
import { SYSTEM_ROLES } from "constants/Constants";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import { LEAD_SOURCES } from "routes/leads/leadSourceRegistry";
import useAuthStore from "stores/useAuthStore";

// Settings pages live under a slug derived from their label unless an explicit
// name is given; the item highlights while any of its sub-routes are mounted.
const Item = ({ children, permissions = [], conditions = [], name = null }) => {
  const to = `/settings/${name ?? snakeCase(children.toLowerCase())}`;

  return (
    <SidebarNavItem to={to} permissions={permissions} conditions={conditions}>
      {children}
    </SidebarNavItem>
  );
};

const SettingsSidebar = () => {
  const user = useAuthStore.getState().userAuth;
  const isAdmin = user?.role?.id === SYSTEM_ROLES.ADMIN;
  const {
    enable_ruvixx_flags_sys_settings,
    enable_leads_feature,
    enable_meta_account,
    enable_swoogo_integration,
  } = useRuvixxFlags();

  return (
    <>
      <SidebarSection label="Admin" icon={<ShieldCheck />}>
        {isAdmin && <Item permissions={[]}>Global Settings</Item>}
        <Item
          name="feature_flags"
          permissions={[ACL_RELATIONSHIPS.featureFlag.read]}
          conditions={[enable_ruvixx_flags_sys_settings]}
        >
          Feature Flags
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.adminUser.read]}>Users</Item>
        <Item
          permissions={[
            ACL_RELATIONSHIPS.adminRole.read,
            ACL_RELATIONSHIPS.adminRolePermission.read,
          ]}
        >
          Roles
        </Item>
        <Item
          permissions={[ACL_RELATIONSHIPS.adminApiKeys.read]}
          name="api_keys"
        >
          API Keys
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.adminGroup.create]}>Groups</Item>
        <Item permissions={[ACL_RELATIONSHIPS.customEmailDomains.readTable]}>
          Custom Email Domains
        </Item>
        <Item
          permissions={[ACL_RELATIONSHIPS.mxDomains.readTable]}
          name="mx_domains"
        >
          MX Domains
        </Item>
        <Item
          permissions={[ACL_RELATIONSHIPS.customTable.readSchema]}
          name="custom_tables"
        >
          Custom Tables
        </Item>
      </SidebarSection>

      <SidebarSection
        label="Call Center"
        icon={<PhoneCall />}
        conditions={[CONFIG.IS_PHONE_ENABLED]}
      >
        <Item permissions={[ACL_RELATIONSHIPS.disposition.read]}>
          Call Dispositions
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.twilioPhoneNumber.readAsTable]}>
          Phone Numbers
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.voicemail.read]}>Voicemails</Item>
      </SidebarSection>

      <SidebarSection label="Campaigns" icon={<Megaphone />}>
        <Item
          permissions={[ACL_RELATIONSHIPS.campaignStatusPreset.readAsTable]}
        >
          Campaign Status Presets
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.campaignStatuses.readAsTable]}>
          Campaign Statuses
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.region.read]}>Regions</Item>
      </SidebarSection>

      <SidebarSection
        label="Constant Contact"
        icon={<Send />}
        conditions={[CONFIG.CONSTANT_CONTACT_ENABLED]}
      >
        <Item
          name="constant_contact_accounts"
          permissions={[ACL_RELATIONSHIPS.constantContactAccounts.read]}
        >
          Accounts
        </Item>
        <Item
          name="constant_contact_campaigns"
          permissions={[ACL_RELATIONSHIPS.constantContactCampaigns.read]}
        >
          Campaigns
        </Item>
      </SidebarSection>

      <SidebarSection label="Integrations" icon={<Blocks />}>
        {LEAD_SOURCES.map(source => (
          <Item
            key={source.key}
            name={`integrations/${source.key}`}
            permissions={[source.manageAcl]}
            conditions={[enable_leads_feature, enable_meta_account]}
          >
            {`${source.label} Accounts`}
          </Item>
        ))}
        <Item
          name="meta_datasets"
          permissions={[ACL_RELATIONSHIPS.metaAdAccount.manageAccounts]}
          conditions={[enable_leads_feature]}
        >
          Meta Datasets
        </Item>
        <Item
          name="meta_leads_events"
          permissions={[ACL_RELATIONSHIPS.metaLeadsEvent.read]}
          conditions={[enable_leads_feature]}
        >
          Meta Leads Events
        </Item>
        <Item
          name="integrations/swoogo"
          permissions={[ACL_RELATIONSHIPS.swoogo.manageConnection]}
          conditions={[enable_swoogo_integration]}
        >
          Swoogo Account
        </Item>
      </SidebarSection>

      <SidebarSection label="Custom Fields" icon={<SlidersHorizontal />}>
        <Item permissions={[ACL_RELATIONSHIPS.customFieldConfig.readTable]}>
          Custom Field Configs
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.customFieldGroup.readTable]}>
          Custom Field Groups
        </Item>
      </SidebarSection>

      <SidebarSection label="Email Assets" icon={<Mail />}>
        <Item permissions={[ACL_RELATIONSHIPS.emailTemplates.read]}>
          Email Templates
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.emailFooters.read]}>
          Email Footers
        </Item>
        <Item
          permissions={[
            ACL_RELATIONSHIPS.emailUrl.readTable,
            ACL_RELATIONSHIPS.emailTemplates.read,
          ]}
        >
          Email URLs
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.emailSignatures.read]}>
          Email Signatures
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.emailAttachments.read]}>
          Email Attachments
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.emailExemption.read]}>
          Email Exemptions
        </Item>
      </SidebarSection>

      <SidebarSection label="Notes" icon={<StickyNote />}>
        <Item permissions={[ACL_RELATIONSHIPS.noteTemplate.read]}>
          Note Templates
        </Item>
      </SidebarSection>

      <SidebarSection
        label="Piracy"
        icon={<Gavel />}
        conditions={[CONFIG.ENABLE_PIRACY]}
      >
        <Item permissions={[ACL_RELATIONSHIPS.MachineApprovalStatus.read]}>
          Machine Approval Statuses
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.sourceStatus.read]}>
          Source Statuses
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.caseDomains.read]}>
          Case Domains
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.investigationStatus.readAsTable]}>
          Investigation Statuses
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.productLine.read]}>
          Product Lines
        </Item>
      </SidebarSection>

      <SidebarSection label="Revenue" icon={<Gem />}>
        <Item permissions={[ACL_RELATIONSHIPS.pointOfContact.read]}>
          Points of Contact
        </Item>
        <Item permissions={[ACL_RELATIONSHIPS.product.read]}>Products</Item>
        <Item permissions={[ACL_RELATIONSHIPS.recipient.read]}>Recipients</Item>
        <Item
          name="revenue_opportunity_recipient_presets"
          permissions={[
            ACL_RELATIONSHIPS.revenueOpportunityRecipientPreset.readAsTable,
          ]}
        >
          Recipient Presets
        </Item>
        <Item
          name="revenue_opportunity_status_presets"
          permissions={[
            ACL_RELATIONSHIPS.revenueOpportunityStatusPreset.readAsTable,
          ]}
        >
          Status Presets
        </Item>
        <Item
          name="revenue_opportunity_statuses"
          permissions={[ACL_RELATIONSHIPS.revenueOpportunityStatus.read]}
        >
          Statuses
        </Item>
      </SidebarSection>

      <SidebarSection label="Tag Management" icon={<Tags />}>
        <Item permissions={[ACL_RELATIONSHIPS.tag.readTable]}>Tags</Item>
        <Item permissions={[ACL_RELATIONSHIPS.tagCategory.readTable]}>
          Tag Categories
        </Item>
      </SidebarSection>
    </>
  );
};

export default SettingsSidebar;
